import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AreaSeries,
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineSeries,
  LineStyle,
  LineType,
  createChart,
  createSeriesMarkers,
  type ISeriesApi,
  type LineData,
  type MouseEventParams,
  type SeriesMarker,
  type Time,
} from 'lightweight-charts'
import {
  chartCandlesForDisplay,
  type PriceLevelSegment,
  type TradeChartDayRow,
  type TradeHistoryAnnotation,
} from '../lib/tradeChartData'

type Props = {
  rows: TradeChartDayRow[]
  annotations: TradeHistoryAnnotation[]
  stopSegments: PriceLevelSegment[]
  takeProfitSegments: PriceLevelSegment[]
  entryY: number
  currency?: string | null
  candleSource?: 'yahoo' | 'history' | 'mixed'
  loading?: boolean
  appearance?: 'classic' | 'studio'
}

const UP = '#089981'
const DOWN = '#F23645'
const ENTRY = '#2962FF'
const MUTED = '#787B86'

const formatPrice = (value: number, currency?: string | null) => {
  const abs = Math.abs(value)
  const digits = abs >= 100 ? 2 : abs >= 1 ? 2 : abs >= 0.01 ? 4 : 6
  const formatted = value.toFixed(digits).replace(/\.?0+$/, '').replace('.', ',')
  if (!currency) return formatted
  const c = currency.toUpperCase()
  if (c === 'USD') return `${formatted} $`
  if (c === 'EUR') return `${formatted} €`
  if (c === 'GBP') return `${formatted} £`
  if (c === 'TRY') return `${formatted} ₺`
  return `${formatted} ${currency}`
}

const trimNumber = (value: number, digits: number) =>
  value.toFixed(digits).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')

const formatCompactMoney = (value: number, currency?: string | null) => {
  if (!Number.isFinite(value)) return '—'
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  const body =
    abs >= 1_000_000
      ? `${trimNumber(abs / 1_000_000, 1)}M`
      : abs >= 10_000
        ? `${trimNumber(abs / 1_000, 1)}k`
        : abs >= 1000
          ? Math.round(abs).toString()
          : trimNumber(abs, abs >= 100 ? 0 : 2)
  const formatted = `${sign}${body}`.replace('.', ',')
  if (!currency) return formatted
  const c = currency.toUpperCase()
  if (c === 'USD') return `${formatted} $`
  if (c === 'EUR') return `${formatted} €`
  if (c === 'GBP') return `${formatted} £`
  if (c === 'TRY') return `${formatted} ₺`
  return `${formatted} ${currency}`
}

function toChartTime(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

function timeToKey(time: Time): string {
  if (typeof time === 'string') return time.slice(0, 10)
  if (typeof time === 'number') return new Date(time * 1000).toISOString().slice(0, 10)
  const month = String(time.month).padStart(2, '0')
  const day = String(time.day).padStart(2, '0')
  return `${time.year}-${month}-${day}`
}

function levelOnDay(segments: PriceLevelSegment[], dayStart: number): number | null {
  const dayEnd = dayStart + 86400000 - 1
  let value: number | null = null
  for (const seg of segments) {
    if (seg.from <= dayEnd && seg.to > dayStart && Number.isFinite(seg.value)) value = seg.value
  }
  return value
}

function historicalLevelPoints(
  rows: TradeChartDayRow[],
  segments: PriceLevelSegment[]
): LineData<Time>[] {
  if (segments.length < 2) return []
  const prior = segments.slice(0, -1)
  const points: LineData<Time>[] = []
  for (const row of rows) {
    const value = levelOnDay(prior, row.time)
    if (value == null) continue
    points.push({ time: toChartTime(row.time), value })
  }
  return points
}

function markerForDay(events: TradeHistoryAnnotation[], time: string): SeriesMarker<Time> {
  const exit = events.some((e) => e.eventType === 'exit' || e.eventType === 'reduce_size' || e.eventType === 'scale_out')
  const entry = events.some((e) => e.eventType === 'entry' || e.eventType === 'add' || e.eventType === 'scale_in')
  if (exit) return { time, position: 'aboveBar', color: DOWN, shape: 'arrowDown' }
  if (entry) return { time, position: 'belowBar', color: UP, shape: 'arrowUp' }
  return { time, position: 'aboveBar', color: ENTRY, shape: 'circle' }
}

function addPriceLine(series: ISeriesApi<'Candlestick'>, price: number, color: string, title: string) {
  if (!Number.isFinite(price) || price <= 0) return
  series.createPriceLine({
    price,
    color,
    lineWidth: 1,
    lineStyle: LineStyle.Dashed,
    axisLabelVisible: true,
    title,
  })
}

export default function TradeDetailChart({
  rows,
  annotations,
  stopSegments,
  takeProfitSegments,
  entryY,
  currency,
  candleSource,
  loading,
  appearance = 'classic',
}: Props) {
  const { t, i18n } = useTranslation('trading')
  const studio = appearance === 'studio'
  const chartHeight = studio ? 400 : 520
  const containerRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<{ time: number; x: number; y: number } | null>(null)

  const priceRows = useMemo(() => chartCandlesForDisplay(rows), [rows])

  const eventsByDay = useMemo(() => {
    const map = new Map<string, TradeHistoryAnnotation[]>()
    const candleDays = priceRows.map((row) => toChartTime(row.time))
    const daySet = new Set(candleDays)
    for (const ann of annotations) {
      const key = toChartTime(ann.time)
      const snapped = daySet.has(key)
        ? key
        : candleDays.filter((day) => day <= key).sort().slice(-1)[0]
      if (!snapped) continue
      const list = map.get(snapped) ?? []
      list.push(ann)
      map.set(snapped, list)
    }
    return map
  }, [annotations, priceRows])

  const labels = useMemo(
    () => ({
      entry: t('tradeDetail.priceChart.refEntry'),
      sl: t('tradeDetail.priceChart.refSl'),
      tp: t('tradeDetail.priceChart.refTp'),
      position: t('tradeDetail.priceChart.axisPosition'),
    }),
    [t]
  )

  useEffect(() => {
    const el = containerRef.current
    if (!el || !priceRows.length) return

    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: '#ffffff' },
        textColor: '#131722',
        fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif',
        fontSize: 12,
        panes: {
          separatorColor: '#E0E3EB',
          separatorHoverColor: 'rgba(178, 181, 189, 0.4)',
        },
      },
      grid: {
        vertLines: { color: '#F0F3FA' },
        horzLines: { color: '#F0F3FA' },
      },
      crosshair: {
        mode: CrosshairMode.MagnetOHLC,
        vertLine: {
          color: '#9598A1',
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: '#131722',
        },
        horzLine: {
          color: '#9598A1',
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: '#131722',
        },
      },
      rightPriceScale: {
        borderColor: '#E0E3EB',
        scaleMargins: { top: 0.08, bottom: 0.06 },
      },
      leftPriceScale: { visible: false },
      timeScale: {
        borderColor: '#E0E3EB',
        rightOffset: 6,
        barSpacing: 9,
        minBarSpacing: 3,
      },
      localization: { locale: i18n.language || 'en' },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: true, pinch: true, axisPressedMouseMove: true },
    })

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: UP,
      downColor: DOWN,
      borderVisible: false,
      wickUpColor: UP,
      wickDownColor: DOWN,
      priceLineVisible: true,
      lastValueVisible: true,
    })

    const candleData = priceRows.map((row) => ({
      time: toChartTime(row.time),
      open: row.open,
      high: Math.max(row.high, row.open, row.close),
      low: Math.min(row.low, row.open, row.close),
      close: row.close,
    }))
    candleSeries.setData(candleData)

    const candleHigh = Math.max(...candleData.map((bar) => bar.high))
    const candleLow = Math.min(...candleData.map((bar) => bar.low))
    const candleSpan = Math.max(candleHigh - candleLow, Math.abs(candleHigh) * 0.02, 0.01)
    let scaleLow = candleLow
    let scaleHigh = candleHigh
    const latestStopLevel = stopSegments.length ? stopSegments[stopSegments.length - 1].value : undefined
    const latestTpLevel = takeProfitSegments.length ? takeProfitSegments[takeProfitSegments.length - 1].value : undefined
    for (const level of [entryY, latestStopLevel, latestTpLevel]) {
      if (level == null || !Number.isFinite(level) || level <= 0) continue
      if (level < candleLow - candleSpan * 1.5 || level > candleHigh + candleSpan * 1.5) continue
      scaleLow = Math.min(scaleLow, level)
      scaleHigh = Math.max(scaleHigh, level)
    }
    const scalePad = (scaleHigh - scaleLow) * 0.08
    candleSeries.applyOptions({
      autoscaleInfoProvider: () => ({
        priceRange: { minValue: scaleLow - scalePad, maxValue: scaleHigh + scalePad },
      }),
    })

    addPriceLine(candleSeries, entryY, ENTRY, labels.entry)
    const latestStop = stopSegments[stopSegments.length - 1]?.value
    const latestTp = takeProfitSegments[takeProfitSegments.length - 1]?.value
    if (latestStop != null) addPriceLine(candleSeries, latestStop, DOWN, labels.sl)
    if (latestTp != null) addPriceLine(candleSeries, latestTp, UP, labels.tp)

    const stopHistory = historicalLevelPoints(priceRows, stopSegments)
    if (stopHistory.length >= 2) {
      const stopSeries = chart.addSeries(LineSeries, {
        color: DOWN,
        lineWidth: 1,
        lineStyle: LineStyle.SparseDotted,
        lineType: LineType.WithSteps,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      })
      stopSeries.setData(stopHistory)
    }

    const tpHistory = historicalLevelPoints(priceRows, takeProfitSegments)
    if (tpHistory.length >= 2) {
      const tpSeries = chart.addSeries(LineSeries, {
        color: UP,
        lineWidth: 1,
        lineStyle: LineStyle.SparseDotted,
        lineType: LineType.WithSteps,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      })
      tpSeries.setData(tpHistory)
    }

    const markers = [...eventsByDay.entries()]
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([time, events]) => markerForDay(events, time))
    if (markers.length) {
      createSeriesMarkers(candleSeries, markers, { zOrder: 'aboveSeries' })
    }

    const firstPosition = priceRows.find((row) => row.positionSize != null && Number.isFinite(row.positionSize))
    const visiblePosition: LineData<Time>[] = []
    if (firstPosition) {
      let lastSize = firstPosition.positionSize ?? 0
      for (const row of priceRows) {
        if (row.time < firstPosition.time) continue
        if (row.positionSize != null && Number.isFinite(row.positionSize)) lastSize = row.positionSize
        visiblePosition.push({ time: toChartTime(row.time), value: lastSize })
      }
    }

    if (visiblePosition.length) {
      const positionSeries = chart.addSeries(
        AreaSeries,
        {
          lineColor: ENTRY,
          topColor: 'rgba(41, 98, 255, 0.28)',
          bottomColor: 'rgba(41, 98, 255, 0.02)',
          lineWidth: 2,
          lineType: LineType.WithSteps,
          priceLineVisible: false,
          lastValueVisible: true,
          crosshairMarkerVisible: false,
          title: labels.position,
          priceFormat: {
            type: 'custom',
            minMove: 0.01,
            formatter: (price: number) => formatCompactMoney(price, currency),
          },
        },
        1
      )
      positionSeries.setData(visiblePosition)
      chart.panes()[0]?.setStretchFactor(3.2)
      chart.panes()[1]?.setStretchFactor(1)
    }

    const rowByDay = new Map(priceRows.map((row) => [toChartTime(row.time), row]))
    const onMove = (param: MouseEventParams<Time>) => {
      if (!param.time || !param.point) {
        setHover(null)
        return
      }
      const row = rowByDay.get(timeToKey(param.time))
      if (!row) {
        setHover(null)
        return
      }
      setHover((current) =>
        current && current.time === row.time ? current : { time: row.time, x: param.point!.x, y: param.point!.y }
      )
    }
    chart.subscribeCrosshairMove(onMove)
    const paint = () => {
      if (el.clientWidth > 0 && el.clientHeight > 0) {
        chart.timeScale().fitContent()
      }
    }
    paint()
    requestAnimationFrame(paint)

    return () => {
      chart.unsubscribeCrosshairMove(onMove)
      chart.remove()
    }
  }, [
    priceRows,
    eventsByDay,
    stopSegments,
    takeProfitSegments,
    entryY,
    currency,
    i18n.language,
    labels,
  ])

  const active = priceRows.find((row) => row.time === hover?.time) ?? priceRows[priceRows.length - 1]
  const hoverEvents = hover ? eventsByDay.get(toChartTime(hover.time)) ?? [] : []
  const tipAbove = (hover?.y ?? 0) > 72
  const tipLeft = (hover?.x ?? 0) > 260
  const activeIndex = active ? priceRows.findIndex((row) => row.time === active.time) : -1
  const prevClose = activeIndex > 0 ? priceRows[activeIndex - 1].close : null
  const changePct =
    active && prevClose != null && prevClose !== 0 ? ((active.close - prevClose) / prevClose) * 100 : null
  const upBar = active ? active.close >= active.open : true

  const sourceLabel =
    candleSource === 'yahoo'
      ? t('tradeDetail.priceChart.sourceYahoo')
      : candleSource === 'history'
        ? t('tradeDetail.priceChart.sourceHistory')
        : candleSource === 'mixed'
          ? t('tradeDetail.priceChart.sourceMixed')
          : null

  if (loading) {
    return (
      <p className="text-sm text-gray-500 py-12 text-center">
        {t('tradeDetail.priceChart.loading')}
      </p>
    )
  }

  if (!priceRows.length) {
    return (
      <p className="text-sm text-gray-500 py-8 text-center">
        {t('tradeDetail.priceChart.noData')}
      </p>
    )
  }

  return (
    <div className={`w-full trade-detail-chart${studio ? ' is-studio' : ''}`}>
      {studio ? (
        <div className="at-td-chart-legend">
          <span><i className="tone-avg" aria-hidden /> {t('tradeDetail.studio.legendAvg')}</span>
          <span><i className="tone-sl" aria-hidden /> {t('tradeDetail.studio.legendSl')}</span>
          <span><i className="tone-exit" aria-hidden /> {t('tradeDetail.studio.legendExit')}</span>
          <span><i className="tone-entry" aria-hidden /> {t('tradeDetail.studio.legendEntryAdd')}</span>
          <span><i className="tone-pos" aria-hidden /> {t('tradeDetail.studio.legendPosition')}</span>
        </div>
      ) : (
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-600 mb-3">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-3 rounded-[1px]" style={{ background: UP }} aria-hidden />
            {t('tradeDetail.priceChart.legendCandleUp')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-3 rounded-[1px]" style={{ background: DOWN }} aria-hidden />
            {t('tradeDetail.priceChart.legendCandleDown')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-8 h-0 border-t border-dashed" style={{ borderColor: ENTRY }} aria-hidden />
            {t('tradeDetail.priceChart.legendEntry')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-8 h-0 border-t border-dashed" style={{ borderColor: DOWN }} aria-hidden />
            {t('tradeDetail.priceChart.legendSl')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-8 h-0 border-t border-dashed" style={{ borderColor: UP }} aria-hidden />
            {t('tradeDetail.priceChart.legendTp')}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-8 h-2 rounded-sm" style={{ background: 'rgba(41, 98, 255, 0.28)' }} aria-hidden />
            {t('tradeDetail.priceChart.legendPosition')}
          </span>
          {sourceLabel && <span className="text-gray-400">{sourceLabel}</span>}
        </div>
      )}

      <div
        className={`trade-detail-tv relative w-full overflow-hidden rounded-lg border border-[#E0E3EB] bg-white${studio ? ' at-td-chart-tv' : ''}`}
        style={{ height: chartHeight }}
      >
        <div ref={containerRef} className="h-full w-full" />
        {active && (
          <div className="pointer-events-none absolute left-3 top-2 z-10 max-w-[calc(100%-88px)] text-[12px] leading-5 text-[#131722]">
            <div className="flex flex-wrap items-baseline gap-x-3">
              <span className="font-semibold">
                {new Date(active.time).toLocaleDateString(i18n.language, {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  timeZone: 'UTC',
                })}
              </span>
              <span style={{ color: upBar ? UP : DOWN }}>
                <span style={{ color: MUTED }}>O</span> {formatPrice(active.open, currency)}{' '}
                <span style={{ color: MUTED }}>H</span> {formatPrice(active.high, currency)}{' '}
                <span style={{ color: MUTED }}>L</span> {formatPrice(active.low, currency)}{' '}
                <span style={{ color: MUTED }}>C</span> {formatPrice(active.close, currency)}
                {changePct != null && (
                  <span>
                    {' '}
                    {changePct >= 0 ? '+' : ''}
                    {changePct.toFixed(2)}%
                  </span>
                )}
              </span>
            </div>
            <div className="text-[11px]" style={{ color: MUTED }}>
              {active.positionSize != null && (
                <span>
                  {t('tradeDetail.priceChart.tooltipPosition')}: {formatCompactMoney(active.positionSize, currency)}
                </span>
              )}
              {active.riskPct != null && (
                <span>
                  {active.positionSize != null ? ' · ' : ''}
                  {t('tradeDetail.priceChart.tooltipRisk')}: {active.riskPct.toFixed(1)}%
                </span>
              )}
            </div>
          </div>
        )}
        {hover && hoverEvents.length > 0 && (
          <div
            className="pointer-events-none absolute z-20 max-w-[240px] rounded-md border border-[#E0E3EB] bg-white px-2.5 py-1.5 text-[11px] leading-4 text-[#131722] shadow-md"
            style={{
              left: hover.x,
              top: hover.y,
              transform: tipLeft
                ? tipAbove
                  ? 'translate(calc(-100% - 14px), calc(-100% - 8px))'
                  : 'translate(calc(-100% - 14px), 14px)'
                : tipAbove
                  ? 'translate(14px, calc(-100% - 8px))'
                  : 'translate(14px, 14px)',
            }}
          >
            <ul className="space-y-1">
              {hoverEvents.map((event, index) => (
                <li key={`${event.time}-${event.eventType}-${index}`}>
                  <span className="font-semibold">{event.title}</span>
                  {event.description && event.description !== event.title ? (
                    <span style={{ color: MUTED }}> — {event.description}</span>
                  ) : null}
                  {event.price != null && Number.isFinite(event.price) ? (
                    <span style={{ color: MUTED }}> · {formatPrice(event.price, currency)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <p className="text-[10px] text-gray-400 mt-2">{t('tradeDetail.priceChart.axisHint')}</p>
    </div>
  )
}
